import { isResponseError, TResponse } from 'models/apiClient';
import {
  IAuthenticationSetupItem,
  IOptionalFieldDetails,
  IOptionalField,
  IQlobalPropertyDetail,
  ItrustedDeviceOptions,
} from 'models/settings';
import { apiClient } from 'utils/apiClient';
import {
  EAuthenticationMethod,
  IFormValues,
} from 'pages/SystemSettingsPage/AuthenticationSetupPage';
import { ApiEndpoints } from 'models/apiEndpoints';

const updateDateTimeSetup = async (dateFormat: string) =>
  apiClient.put<TResponse<IFormValues>>(
    `${ApiEndpoints.UPDATE_DATE_TIME_SETUP()}?value=${dateFormat}`,
  );

const getDateTimeSetupDetail = async (): Promise<IQlobalPropertyDetail | null> => {
  const { data } = await apiClient.get<TResponse<IQlobalPropertyDetail>>(
    ApiEndpoints.GET_DATE_TIME_SETUP(),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const updateTwoFactorAuthentication = async (authenticationMethod: string) => {
  await apiClient.put<TResponse<IFormValues>>(
    `${ApiEndpoints.UPDATE_TWO_FA()}?option=${authenticationMethod}`,
  );
};

const updateAuthenticationSetup = async (data: any) => {
  if (data.twoFactorAuthentication && data.twoFactorAuthentication === 'true') {
    await updateTwoFactorAuthentication(data.authenticationMethod);
  } else if (data.twoFactorAuthentication && data.twoFactorAuthentication === 'false') {
    await updateTwoFactorAuthentication(EAuthenticationMethod.NONE);
  }

  if (data.timePeriods) {
    const timePeriods: ItrustedDeviceOptions[] = Object.values(data.timePeriods);
    await apiClient.put<TResponse<IFormValues>>(
      `${ApiEndpoints.UPDATE_TRUSTED_DEVICE_OPTIONS()}`,
      timePeriods,
    );
  }
  if (data.numberOfInvalidLoginAttempts) {
    await apiClient.put<TResponse<IFormValues>>(
      `${ApiEndpoints.UPDATE_ATTEMPTS_TO_BLOCK()}?value=${data.numberOfInvalidLoginAttempts}`,
    );
  }
  if (data.timePeriodOfAccountLockout) {
    await apiClient.put<TResponse<IFormValues>>(
      `${ApiEndpoints.UPDATE_MINUTES_IN_BAN()}?value=${data.timePeriodOfAccountLockout}`,
    );
  }

  return true;
};
const getAuthenticationSetupItem = async (
  endpoint: string,
): Promise<IQlobalPropertyDetail | null> => {
  const { data } = await apiClient.get<IQlobalPropertyDetail>(endpoint);

  return data;
};

const getAuthenticationSetupItemAuthenticationMethod =
  async (): Promise<EAuthenticationMethod | null> => {
    const { data } = await apiClient.get<EAuthenticationMethod | null>(
      ApiEndpoints.GET_METHOD_AUTH(),
    );
    return data;
  };
const getAuthenticationSetupItemTrustedDevice = async (): Promise<
  ItrustedDeviceOptions[] | null
> => {
  const { data } = await apiClient.get<ItrustedDeviceOptions[]>(
    ApiEndpoints.GET_TRUSTED_DEVICE_OPTIONS(),
  );
  return data;
};

const getAuthenticationSetupDetail = async (): Promise<IAuthenticationSetupItem | null> => {
  const result: IAuthenticationSetupItem = {};

  const minutesInBanData: IQlobalPropertyDetail | null = await getAuthenticationSetupItem(
    ApiEndpoints.GET_MINUTES_IN_BAN(),
  );
  if (minutesInBanData) {
    result[`${minutesInBanData.key}`] = minutesInBanData.value;
  }

  const attemptsToBlockData: IQlobalPropertyDetail | null = await getAuthenticationSetupItem(
    ApiEndpoints.GET_ATTEMPTS_TO_BLOCK(),
  );
  if (attemptsToBlockData) {
    result[`${attemptsToBlockData.key}`] = attemptsToBlockData.value;
  }

  const authMethod: EAuthenticationMethod | null =
    await getAuthenticationSetupItemAuthenticationMethod();

  if (authMethod) {
    result.two_fa_enabled = authMethod === EAuthenticationMethod.NONE ? 'false' : 'true';
    result.authentication_method = authMethod;
  }

  const data: ItrustedDeviceOptions[] | null = await getAuthenticationSetupItemTrustedDevice();
  if (data && data.length) {
    const timePeriods: IAuthenticationSetupItem = [];
    // eslint-disable-next-line array-callback-return
    data.map((option) => {
      timePeriods[`${option.daysToExpire}`] = option.enabled;
    });
    result.timePeriods = timePeriods;
  }

  if (isResponseError(result)) {
    return null;
  }

  return result;
};

const getTrustedDeviceOptions = async (): Promise<ItrustedDeviceOptions[] | null> => {
  const { data } = await apiClient.get<TResponse<ItrustedDeviceOptions[]>>(
    ApiEndpoints.GET_TRUSTED_DEVICE_OPTIONS_LIST(),
  );
  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const getEnabledTrustedDeviceOptions = async (): Promise<ItrustedDeviceOptions[] | null> => {
  const { data } = await apiClient.get<TResponse<ItrustedDeviceOptions[]>>(
    ApiEndpoints.GET_ENABLED_TRUSTED_DEVICE_OPTIONS_LIST(),
  );
  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const getTimeZoneInfo = async (): Promise<string | null> => {
  const { data } = await apiClient.get<TResponse<string>>(ApiEndpoints.GET_TIME_ZONE());
  if (isResponseError(data)) {
    return null;
  }
  return data;
};

const updateNotificationsSetup = async (value: string) =>
  apiClient.put<TResponse<IFormValues>>(
    `${ApiEndpoints.UPDATE_NOTIFICATION_SETUP()}?notificationCron=${value}`,
  );

const updateETraceSetup = async (value: boolean) =>
  apiClient.put<TResponse<IQlobalPropertyDetail>>(
    `${ApiEndpoints.UPDATE_ETRACE_SETUP()}?value=${value}`,
  );

const updateIArmsSetup = async (value: boolean) =>
  apiClient.put<TResponse<IQlobalPropertyDetail>>(
    `${ApiEndpoints.UPDATE_IARMS_SETUP()}?value=${value}`,
  );

const getNotificationsSetup = async (): Promise<IQlobalPropertyDetail | null> => {
  const { data } = await apiClient.get<TResponse<IQlobalPropertyDetail>>(
    ApiEndpoints.GET_NOTIFICATION_SETUP(),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getETraceSetup = async (): Promise<IQlobalPropertyDetail | null> => {
  const { data } = await apiClient.get<TResponse<IQlobalPropertyDetail>>(
    ApiEndpoints.GET_ETRACE_SETUP(),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getIArmsSetup = async (): Promise<IQlobalPropertyDetail | null> => {
  const { data } = await apiClient.get<TResponse<IQlobalPropertyDetail>>(
    ApiEndpoints.GET_IARMS_SETUP(),
  );

  if (isResponseError(data)) {
    return null;
  }

  return data;
};

const getCurrencySetup = async (): Promise<IQlobalPropertyDetail | null> => {
  const { data } = await apiClient.get<TResponse<IQlobalPropertyDetail>>(
    ApiEndpoints.GET_CURRENCY(),
  );
  if (isResponseError(data)) {
    return null;
  }
  return data;
};
const updateCurrencySetup = async (value: string) =>
  apiClient.put<TResponse<IQlobalPropertyDetail>>(
    `${ApiEndpoints.UPDATE_CURRENCY()}?dictionaryUniqueId=${value}`,
  );

const getMeasurementSetup = async (): Promise<IQlobalPropertyDetail | null> => {
  const { data } = await apiClient.get<TResponse<IQlobalPropertyDetail>>(
    ApiEndpoints.GET_MEASUREMENT(),
  );
  if (isResponseError(data)) {
    return null;
  }
  return data;
};
const updateMeasurementSetup = async (value: string) =>
  apiClient.put<TResponse<IQlobalPropertyDetail>>(
    `${ApiEndpoints.UPDATE_MEASUREMENT()}?dictionaryUniqueId=${value}`,
  );

const getSystemFieldsConfiguration = async (
  modules: string[],
): Promise<IOptionalFieldDetails[] | null> => {
  const modulesQuery = modules.map((module) => `modules=${module}`);
  const { data } = await apiClient.get<TResponse<IOptionalFieldDetails[]>>(
    `${ApiEndpoints.GET_OPTIONAL_FIELDS()}?${modulesQuery.join('&')}`,
  );
  if (isResponseError(data)) {
    return null;
  }
  return data;
};
const updateSystemFieldsConfiguration = async (values: IOptionalField[]) =>
  apiClient.patch<TResponse<any>>(ApiEndpoints.UPDATE_OPTIONAL_FIELDS(), values);

export {
  updateDateTimeSetup,
  getDateTimeSetupDetail,
  updateAuthenticationSetup,
  getAuthenticationSetupDetail,
  getEnabledTrustedDeviceOptions,
  getTrustedDeviceOptions,
  updateNotificationsSetup,
  getNotificationsSetup,
  getTimeZoneInfo,
  getIArmsSetup,
  updateIArmsSetup,
  getETraceSetup,
  updateETraceSetup,
  getCurrencySetup,
  updateCurrencySetup,
  getMeasurementSetup,
  updateMeasurementSetup,
  getSystemFieldsConfiguration,
  updateSystemFieldsConfiguration,
};
